import type { OfferCartRequest } from '../../features/regularization/offer/types';

/**
 * The cart the checkout has to build when it opens.
 *
 * Contract: a purchase surface does NOT build the cart before opening the
 * checkout. It opens (`useOpenCheckout`) with the payload, the checkout paints
 * on the same click with its loading screen, and the build runs inside
 * (`useApplyPendingCart`). The surface gets a promise with the order in case
 * it needs the result (e.g. checking the package price), without awaiting it
 * to open.
 *
 * It lives in module memory, not in sessionStorage or redux, on purpose:
 *  - sessionStorage survives a refresh, and a package cart must never be built
 *    again with a stale charge. That belongs to URL intents, which are
 *    campaigns.
 *  - The promise the requester gets is not serializable. Only the id goes to
 *    redux (`plansUI.pendingCartId`), which is what the render needs.
 *
 * One owner per request: whoever takes it with `claimPendingCart` builds it
 * even if the checkout unmounts, so closing and reopening mid-build doesn't
 * repeat it. And builds are queued: two at once would close each other's cart.
 */

/**
 * A cart the engine builds itself: the Regularización total package. The
 * surface sends the charge the user accepted (`POST /regularization/cart`),
 * never its lines or prices.
 */
export type PackageCartBuild = { packageCart: OfferCartRequest };

/** What the checkout builds: item lines added to a cart, or a package cart the
 * engine builds. */
export type CartContents = unknown[] | PackageCartBuild;

export const isPackageCartBuild = (
	contents: CartContents,
): contents is PackageCartBuild =>
	!Array.isArray(contents) && typeof contents === 'object' && contents !== null && 'packageCart' in contents;

type PendingCartRequest = {
	id: number;
	payload: CartContents;
	/** When the surface asked for it (ms epoch): the user's wait starts here. */
	stagedAt: number;
	resolve: (orders: unknown[]) => void;
	reject: (error: unknown) => void;
};

/** The request was replaced by another before being built. Not an error to
 * show the user. */
export class PendingCartSupersededError extends Error {
	constructor() {
		super('Another cart was requested before this one was built');
	}
}

let nextRequestId = 1;
let staged: PendingCartRequest | null = null;
let previousBuild: Promise<void> = Promise.resolve();

export const stagePendingCart = (payload: CartContents) => {
	staged?.reject(new PendingCartSupersededError());
	let resolve!: PendingCartRequest['resolve'];
	let reject!: PendingCartRequest['reject'];
	const built = new Promise<unknown[]>((onResolve, onReject) => {
		resolve = onResolve;
		reject = onReject;
	});
	staged = { id: nextRequestId++, payload, stagedAt: Date.now(), resolve, reject };
	return { requestId: staged.id, built };
};

export const claimPendingCart = (): PendingCartRequest | null => {
	const request = staged;
	staged = null;
	return request;
};

/** Runs the build once the previous one finishes. `build` handles its own
 * errors: the queue never breaks. */
export const afterPreviousBuild = (build: () => Promise<void>) => {
	previousBuild = previousBuild.then(build);
};
